import { Component, OnInit, ViewChild, Renderer2 } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Router } from '@angular/router';
import { GiftCardsService } from '../services/gift-cards.service';
import { GiftCardsComponent } from './gift-cards/gift-cards.component';
import { PlaygroundService } from '../services/playgrounds.service';
import { CognitoCredentialsService } from '../services/cognito-credentials.service';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit {

  constructor(
    private afAuth: AngularFireAuth,
    private router: Router,
    private giftCardsService: GiftCardsService,
    private renderer: Renderer2,
    private playgroundService: PlaygroundService,
    private cognitoCredentials: CognitoCredentialsService,
  ) { }

  businessData: any;
  availableFeatures!: string[];
  giftCards: any = [];
  businessId!: string;
  activationCode!: string;
  loading = false;
  activeTab!: boolean;
  hasBirthdayCards: boolean = false;
  hasExtraGameCards: boolean = false;
  hasOtherCards: boolean = false;
  formattedDate: string | null = '';
  isMenuOpen = false;
  activeFeature: string = '';
  @ViewChild(GiftCardsComponent) giftComponent!: GiftCardsComponent;

  logout() {
    // Clear Cognito credentials cache
    this.cognitoCredentials.clearCache();
    
    this.afAuth.signOut().then(() => {
      this.giftCards = [];
      localStorage.clear();
      this.router.navigate(['/login']);
    }).catch((error) => {
      console.error('Logout failed', error);
    });
  }

  isAdmin: boolean = false;
  isSuperAdmin: boolean = false;
  isOrdersAdmin: boolean = false;
  playgrounds: any[] = [];
  filteredPlaygrounds: any[] = [];
  showSwitcher: boolean = false;
  allPlaygroundsSelected: boolean = false;

  ngOnInit(): void {

    this.playgroundService.playground$.subscribe(playground => {
      this.businessData = playground;
      this.businessId = playground?.id;
      this.activationCode = playground?.activationCode
    });

    const sessionFeatures = localStorage.getItem("af");
    if (sessionFeatures) {
      this.availableFeatures = JSON.parse(sessionFeatures);
      // Set default activeFeature to 'orders' if available (ordersAdmin also uses the orders pane)
      if (this.availableFeatures.includes('orders') || this.availableFeatures.includes('ordersAdmin')) {
        this.activeFeature = 'orders';
      } else if (this.availableFeatures.length > 0) {
        this.activeFeature = this.availableFeatures[0];
      }
    } 

    // Set isAdmin, isSuperAdmin and isOrdersAdmin based on user role / features
    const role = localStorage.getItem('userRole');
    this.isAdmin = role === 'admin' || role === 'superAdmin';
    this.isSuperAdmin = role === 'superAdmin';
    this.isOrdersAdmin = this.availableFeatures.includes('ordersAdmin');
    // Load playgrounds for superAdmin and ordersAdmin (switcher visibility)
    if (this.isSuperAdmin || this.isOrdersAdmin) {
      const playgrounds = localStorage.getItem('playgrounds');
      if (playgrounds) {
        this.playgrounds = JSON.parse(playgrounds);
        this.filteredPlaygrounds = [...this.playgrounds];
      }
      // Determine if all playgrounds are selected (for ordersAdmin display)
      if (this.isOrdersAdmin && this.playgrounds.length > 1) {
        const mpRaw = localStorage.getItem('mp');
        if (mpRaw) {
          const selectedIds: string[] = JSON.parse(mpRaw);
          const allIds = this.playgrounds.map((p: any) => p.uid ?? p.id).filter(Boolean);
          this.allPlaygroundsSelected = selectedIds.length === allIds.length &&
            allIds.every((id: string) => selectedIds.includes(id));
        }
      }
    }
  }

  loadGiftCards(): void {
    const cachedGiftCards = this.giftCardsService.getCachedGiftCards();

    if (cachedGiftCards.length > 0) {
      this.giftCards = cachedGiftCards;
      this.processGiftCards();
    } else {
      this.loading = true;
      this.giftCardsService.getGiftCards(this.businessId).then(
        (cards) => {
          this.giftCards = cards;
          this.loading = false;
          this.processGiftCards();
        },
        (error) => {
          console.error('Error fetching vouchers from Firestore', error);
          this.loading = false;
        }
      );
    }
  }

  processGiftCards(): void {
    if (this.giftCards && this.giftCards.length > 0) {
      this.hasBirthdayCards = this.giftCards.some((card: { type: string }) => card.type === 'birthday');
      this.hasExtraGameCards = this.giftCards.some((card: { type: string }) => card.type === 'game');
      this.hasOtherCards = this.giftCards.some((card: { type: string }) => card.type === 'other');
      setTimeout(() => {
        this.giftComponent.filterGiftCards();
      }, 100);
    }
  }

  checkTabStatus(newTabStatus : boolean) {
    this.activeTab = newTabStatus;
  }

  openPlaygroundSwitcher() {
    this.showSwitcher = true;
    this.renderer.addClass(document.body, 'modal-open');
  }

  closePlaygroundSwitcher() {
    this.showSwitcher = false
    this.renderer.removeClass(document.body, 'modal-open');
  }

  switchPlayground(selectedBusiness: any) {
    localStorage.setItem('businessData', JSON.stringify(selectedBusiness));
    if (this.isOrdersAdmin && selectedBusiness?.uid) {
      localStorage.setItem('mp', JSON.stringify([selectedBusiness.uid]));
    }
    localStorage.removeItem('giftCards')
    // localStorage.setItem('af', JSON.stringify(selectedBusiness.availableFeatures));
    this.closePlaygroundSwitcher();
    window.location.reload();
  }

  resetToAllPlaygrounds() {
    if (!this.playgrounds?.length) return;
    const allUids = this.playgrounds.map((p: any) => p.uid ?? p.id).filter(Boolean);
    localStorage.setItem('mp', JSON.stringify(allUids));
    const first = this.playgrounds[0];
    const businessData = {
      id: first.uid ?? first.id,
      uid: first.uid ?? first.id,
      name: first.name ?? first.fullName ?? '',
      businessImageUrl: first.businessImageUrl ?? '',
      ...first
    };
    localStorage.setItem('businessData', JSON.stringify(businessData));
    localStorage.removeItem('giftCards');
    this.closePlaygroundSwitcher();
    window.location.reload();
  }

  searchPlaygrounds(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input?.value.toLowerCase() || '';
    this.filteredPlaygrounds = this.playgrounds.filter((playground) => {
      if (playground.name) {
        return playground.name.toLowerCase().includes(searchTerm)
      }
      return false;
    });
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
  }

  setActiveFeature(feature: string) {
    this.activeFeature = feature;
  }

}
